// frontend/src/pages/admin/EmailTemplateManager.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, Preview } from "@mui/icons-material";
import { adminAPI } from "../../services/api";
import ReactMarkdown from "react-markdown";

const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailTemplates();
      setTemplates(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (selectedTemplate.id) {
        await adminAPI.updateEmailTemplate(
          selectedTemplate.id,
          selectedTemplate
        );
      } else {
        await adminAPI.createEmailTemplate(selectedTemplate);
      }
      await fetchTemplates();
      setEditMode(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        setLoading(true);
        await adminAPI.deleteEmailTemplate(id);
        await fetchTemplates();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Email Template Manager
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Templates
            </Typography>
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template.id}
                  selected={selectedTemplate?.id === template.id}
                  onClick={() => setSelectedTemplate(template)}
                  button
                >
                  <ListItemText
                    primary={template.name}
                    secondary={template.description}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => {
                        setSelectedTemplate(template);
                        setEditMode(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setSelectedTemplate(template);
                        setPreviewMode(true);
                      }}
                    >
                      <Preview />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(template.id)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() => {
                setSelectedTemplate({
                  name: "",
                  description: "",
                  subject: "",
                  content: "",
                  variables: [],
                });
                setEditMode(true);
              }}
            >
              Add New Template
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedTemplate && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Template Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={selectedTemplate.name}
                    onChange={(e) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        name: e.target.value,
                      })
                    }
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={selectedTemplate.description}
                    onChange={(e) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        description: e.target.value,
                      })
                    }
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={selectedTemplate.subject}
                    onChange={(e) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        subject: e.target.value,
                      })
                    }
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    label="Content"
                    value={selectedTemplate.content}
                    onChange={(e) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        content: e.target.value,
                      })
                    }
                    disabled={!editMode}
                    helperText="Supports HTML and template variables (e.g., {{userName}})"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Variables"
                    value={selectedTemplate.variables.join(", ")}
                    onChange={(e) =>
                      setSelectedTemplate({
                        ...selectedTemplate,
                        variables: e.target.value
                          .split(",")
                          .map((v) => v.trim()),
                      })
                    }
                    disabled={!editMode}
                    helperText="Comma-separated list of available variables"
                  />
                </Grid>
              </Grid>
              {editMode && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : "Save"}
                  </Button>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewMode}
        onClose={() => setPreviewMode(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Template Preview: {selectedTemplate?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Subject: {selectedTemplate?.subject}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <ReactMarkdown>{selectedTemplate?.content}</ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMode(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateManager;
